import { useState } from "react";
import { FiDollarSign, FiActivity, FiFileText } from "react-icons/fi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import type { PortfolioAggregate } from "../api/portfolio";
import {
  buildImpactChartData,
  buildImpactChartOptions,
  buildImpactRangeRows,
  IMPACT_DESCRIPTIONS,
  type ImpactMetric,
} from "./portfolioImpactChart";
import { useRangeMode } from "../../impact/context/RangeModeContext";
import { RangeBar } from "../../impact/components/RangeVisuals";
import { isRangeReady, type ScenariosBlock } from "../../impact/domain/scenarios";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type MetricTab = ImpactMetric;

interface Props {
  impacts: PortfolioAggregate["impacts"];
  analysisMode?: "ctg" | "ops";
  scenarios?: ScenariosBlock;
}

export function PortfolioImpactExplorer({ impacts, analysisMode = "ctg", scenarios }: Props) {
  const [activeTab, setActiveTab] = useState<MetricTab>("economic");
  const { mode: rangeMode } = useRangeMode();
  const showRange = rangeMode === "range" && isRangeReady(scenarios);
  const rangeStrip = showRange ? buildImpactRangeRows(scenarios, activeTab) : null;


  const { hasData, data: chartData } = buildImpactChartData(impacts, activeTab);
  const chartOptions = buildImpactChartOptions(activeTab);
  const descriptions = IMPACT_DESCRIPTIONS;
  const hasNegativeBars = (chartData.datasets[0]?.data ?? []).some(
    (v) => (v as number) < 0,
  );

  return (
    <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
      <div className="p-6 pb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-medium text-text-primary">
            Portfolio Impact Explorer
          </h2>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            analysisMode === "ops"
              ? "bg-amber-500/15 text-amber-400"
              : "bg-quantum-500/15 text-quantum-400"
          }`}>
            {analysisMode === "ops" ? "Operations" : "Cradle to Grave"}
          </span>
        </div>
      </div>

      <div className="px-6 pt-1 pb-2">
        <p className="text-sm text-text-secondary">
          {descriptions[activeTab]}
        </p>
      </div>

      <div className="px-6">
        <div className="h-80 w-full">
          {hasData ? (
            <Bar data={chartData} options={chartOptions} height={300} />
          ) : (
            <div className="flex items-center justify-center h-full text-text-muted text-sm">
              No impact data available for filtered projects
            </div>
          )}
        </div>
        {hasNegativeBars && hasData && (
          <p className="text-[11px] text-text-muted mt-1 mb-0">
            Colored bars = avoided impact &middot; Red bars = net burden from
            infrastructure or displaced clean sources
          </p>
        )}
      </div>

      {rangeStrip && rangeStrip.rows.length > 0 && (
        <div className="px-6 mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1">
            Sensitivity range · Floor–Ceiling
          </p>
          <div>
            {rangeStrip.rows.map((r) => (
              <RangeBar
                key={r.category}
                label={r.label}
                floor={r.floor}
                central={r.central}
                ceiling={r.ceiling}
                max={rangeStrip.max}
                color={rangeStrip.color}
                format={rangeStrip.format}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-t border-border mt-4">
        <button
          onClick={() => setActiveTab("economic")}
          className={`flex items-center justify-center gap-2 flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === "economic"
              ? "text-economic-400 border-t-2 border-economic-400"
              : "text-text-muted hover:text-economic-400"
          }`}
        >
          <FiDollarSign />
          Economic
        </button>
        <button
          onClick={() => setActiveTab("dalys")}
          className={`flex items-center justify-center gap-2 flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === "dalys"
              ? "text-health-400 border-t-2 border-health-400"
              : "text-text-muted hover:text-health-400"
          }`}
        >
          <FiActivity />
          Health
        </button>
        <button
          onClick={() => setActiveTab("pdfs")}
          className={`flex items-center justify-center gap-2 flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === "pdfs"
              ? "text-ecosystem-400 border-t-2 border-ecosystem-400"
              : "text-text-muted hover:text-ecosystem-400"
          }`}
        >
          <FiFileText />
          Biodiversity
        </button>
      </div>
    </div>
  );
}
